/**
 * Fields which only apply while a checkbox somewhere above them is ticked.
 *
 * The block stays on the screen and stays in the tab order - it is greyed out
 * rather than taken away, so an editor sees what ticking the box would ask of
 * them before they tick it.
 *
 * The attributes, all of them on the driving checkbox, radio or select:
 *     data-reveal="#block"      the block it governs, as a selector
 *     data-reveal-value="A,B"   which options of a select put it in play
 *     data-reveal-invert        the block applies while the box is UNticked
 *     data-reveal-mode="disable"  the fields must not be submitted at all
 *     data-reveal-mode="dim"      grey it out, but lock nothing
 *
 * The default mode never disables a field carrying text. A disabled control
 * is not submitted, and an endpoint cannot tell a field which was left out
 * from one which was emptied, so disabling can quietly erase what is stored.
 * `readonly` keeps the value travelling with the form. `disable` is the
 * opt-in for a block whose values genuinely mean nothing while it is greyed -
 * the schedule of a post which is already published, say.
 *
 * `dim` greys the block and touches nothing else. It is for a block which is
 * not the one in play but is still the editor's to write: the notice of a
 * lockdown mode which is not switched on has to be writable while another
 * one is live, or it could only ever be written with the portal already
 * closed behind it.
 *
 * Blocks nest. A block inside a greyed block is greyed too, because a driver
 * which is itself disabled governs nothing.
 */

import { queryAll } from "../core/dom";

/** Class marking a block whose fields do not apply right now. */
const INACTIVE = "is-inactive";

/** Fields made read only, because they carry a value worth submitting. */
const WRITABLE = "input:not([type=checkbox]):not([type=radio]), textarea";

/** Fields with no read only state of their own, disabled instead. */
const SWITCHES = "input[type=checkbox], input[type=radio], select";

/** What being greyed out costs the fields of a block. */
export type RevealMode = "readonly" | "disable" | "dim";

/** Return the mode behind an attribute, defaulting to the read only one. */
export function revealMode(value: string | undefined): RevealMode {
    return value === "disable" || value === "dim" ? value : "readonly";
}

/** A control which may govern a block: a checkbox, a radio or a select. */
export type RevealToggle = HTMLInputElement | HTMLSelectElement;

/** One control and the block it governs. */
export interface RevealPair {
    readonly toggle: RevealToggle;
    readonly block: HTMLElement;
    readonly inverted: boolean;
    readonly mode: RevealMode;
}

/** Grey `block` out, or bring it back. */
export function setInactive(block: HTMLElement, inactive: boolean, mode: RevealMode = "readonly"): void {
    block.classList.toggle(INACTIVE, inactive);

    // Dimming is a statement rather than a lock: it says these fields are not
    // the ones in play, and leaves every one of them exactly as usable.
    if (mode === "dim") {
        return;
    }

    for (const field of queryAll<HTMLInputElement | HTMLTextAreaElement>(WRITABLE, block)) {
        field.readOnly = inactive && mode !== "disable";
        field.disabled = inactive && mode === "disable";
    }

    for (const field of queryAll<HTMLInputElement | HTMLSelectElement>(SWITCHES, block)) {
        field.disabled = inactive;
    }
}

/** Return the block a toggle governs, or `null` when there is none. */
export function blockOf(toggle: HTMLElement): HTMLElement | null {
    const selector = toggle.dataset["reveal"] ?? "";
    if (!selector) {
        return null;
    }

    try {
        return document.querySelector<HTMLElement>(selector);
    } catch {
        // A typo in a template must not take the rest of the bundle with it.
        return null;
    }
}

/**
 * Return whether the control itself is asking for its block.
 *
 * A box asks for it by being ticked. A select asks for it by standing on one
 * of the options named in `data-reveal-value`, which is how a field that only
 * means something for one choice - the role floor of a page nobody but the
 * admin team may open - is greyed for every other choice.
 */
function chosen(toggle: RevealToggle): boolean {
    if (toggle.tagName !== "SELECT") {
        return (toggle as HTMLInputElement).checked;
    }

    const wanted = (toggle.dataset["revealValue"] ?? "")
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value !== "");

    return wanted.includes((toggle as HTMLSelectElement).value);
}

/**
 * Return whether `pair` should have its block in play.
 *
 * A disabled toggle governs nothing: it sits inside a block which is itself
 * greyed out, and letting it speak would un-grey a block its own parent has
 * just put away.
 */
export function isActive(pair: RevealPair): boolean {
    if (pair.toggle.disabled) {
        return false;
    }

    return pair.inverted ? !chosen(pair.toggle) : chosen(pair.toggle);
}

/** Selector of every control which may govern a block. */
const DRIVERS = "input[type=checkbox][data-reveal], input[type=radio][data-reveal], select[data-reveal]";

/** Return every driving control of `root` paired with its block. */
export function revealPairs(root: ParentNode = document): RevealPair[] {
    const pairs: RevealPair[] = [];

    for (const toggle of queryAll<RevealToggle>(DRIVERS, root)) {
        const block = blockOf(toggle);
        if (!block) {
            continue;
        }

        pairs.push({
            toggle,
            block,
            inverted: toggle.hasAttribute("data-reveal-invert"),
            mode: revealMode(toggle.dataset["revealMode"]),
        });
    }

    return pairs;
}

/**
 * Apply every pair, in the order the page renders them.
 *
 * All of them run on every change rather than only the one which moved: a
 * nested block has to be re-decided after the block holding it, and document
 * order is what puts the outer one first.
 */
export function applyReveals(pairs: readonly RevealPair[]): void {
    for (const pair of pairs) {
        setInactive(pair.block, !isActive(pair), pair.mode);
    }
}

/**
 * Wire every block governed by a control of the current page.
 *
 * Every pair is applied on every change, which is what a radio group needs:
 * the browser fires the event on the button which was picked and says nothing
 * about the one which just lost its tick.
 */
export function initReveals(): void {
    const pairs = revealPairs();
    if (pairs.length === 0) {
        return;
    }

    const apply = (): void => applyReveals(pairs);

    for (const pair of pairs) {
        pair.toggle.addEventListener("change", apply);
    }

    // The same call decides the state on load and on every tick, so the two
    // can never drift apart.
    apply();
}
